/**
 * On-air presentation semantics.
 *
 * The design directive is explicit: a state must never rely on colour alone. Every mode
 * therefore carries an icon name, a label and a token, and the components render all
 * three. These names are also what the future SwiftUI and Compose clients map to.
 */

export type OnAirMode = 'AUTO' | 'LIVE' | 'REMOTE' | 'EMERGENCY' | 'FALLBACK' | 'OFFLINE';

export interface OnAirPresentation {
  label: string;
  /** lucide icon name */
  icon: string;
  /** modifier class from the component layer */
  className: string;
  /** Whether this mode raises a banner that no layout may hide. */
  raisesBanner: boolean;
  bannerTone: 'danger' | 'warning' | 'info' | null;
  /** Text announced to screen readers when the mode changes. */
  announcement: string;
}

export const ON_AIR_PRESENTATION: Readonly<Record<OnAirMode, OnAirPresentation>> = {
  AUTO: {
    label: 'AUTO',
    icon: 'AudioWaveform',
    className: 'shio-status--auto',
    raisesBanner: false,
    bannerTone: null,
    announcement: 'Automated playout is on air.',
  },
  LIVE: {
    label: 'LIVE',
    icon: 'Mic',
    className: 'shio-status--live',
    raisesBanner: false,
    bannerTone: null,
    announcement: 'A live source is on air.',
  },
  REMOTE: {
    label: 'REMOTE',
    icon: 'Satellite',
    className: 'shio-status--remote',
    raisesBanner: false,
    bannerTone: null,
    announcement: 'A remote presenter is on air.',
  },
  EMERGENCY: {
    label: 'EMERGENCY',
    icon: 'Siren',
    className: 'shio-status--emergency',
    raisesBanner: true,
    bannerTone: 'danger',
    announcement: 'Emergency source is on air. Immediate attention required.',
  },
  FALLBACK: {
    label: 'FALLBACK',
    icon: 'ShieldAlert',
    className: 'shio-status--fallback',
    raisesBanner: true,
    bannerTone: 'warning',
    announcement: 'The fallback source is carrying the stream.',
  },
  OFFLINE: {
    label: 'OFFLINE',
    icon: 'CircleSlash',
    className: 'shio-status--offline',
    raisesBanner: true,
    bannerTone: 'danger',
    announcement: 'The station is not broadcasting.',
  },
};

/** Content types, for the timeline and the clockwheel. */
export type ContentType =
  | 'unknown'
  | 'show'
  | 'music'
  | 'jingle'
  | 'sweeper'
  | 'commercial'
  | 'promo'
  | 'news'
  | 'podcast'
  /** A live studio or remote source, which is never the same thing as a scheduled show. */
  | 'live'
  | 'emergency';

export interface ContentPresentation {
  label: string;
  icon: string;
  /** CSS custom property carrying this type's hue. */
  token: string;
}

export const CONTENT_PRESENTATION: Readonly<Record<ContentType, ContentPresentation>> = {
  // Used when the source genuinely does not know. Labelling an unknown item "Music"
  // would be a guess presented to an operator as fact.
  unknown: { label: '—', icon: 'Circle', token: '--shio-event-locked' },
  show: { label: 'Show', icon: 'Radio', token: '--shio-content-show' },
  music: { label: 'Music', icon: 'Music', token: '--shio-content-music' },
  jingle: { label: 'Jingle', icon: 'Bell', token: '--shio-content-jingle' },
  sweeper: { label: 'Sweeper', icon: 'Zap', token: '--shio-content-sweeper' },
  commercial: {
    label: 'Commercial',
    icon: 'Megaphone',
    token: '--shio-content-commercial',
  },
  promo: { label: 'Promo', icon: 'Sparkles', token: '--shio-content-promo' },
  news: { label: 'News', icon: 'Newspaper', token: '--shio-content-news' },
  podcast: {
    label: 'Podcast',
    icon: 'Podcast',
    token: '--shio-content-podcast',
  },
  live: { label: 'Live', icon: 'Mic', token: '--shio-source-live' },
  emergency: {
    label: 'Emergency',
    icon: 'Siren',
    token: '--shio-content-emergency',
  },
};

/** Whether a string from the API is a content type we can present. */
export function isContentType(value: string): value is ContentType {
  return value in CONTENT_PRESENTATION;
}

/** Where a timeline item sits relative to now. */
export type PlayoutItemState = 'aired' | 'on_air' | 'queued' | 'locked' | 'blocked';

export const PLAYOUT_ITEM_LABEL: Readonly<Record<PlayoutItemState, string>> = {
  aired: 'Aired',
  on_air: 'On air',
  queued: 'Queued',
  locked: 'Locked',
  blocked: 'Blocked',
};

/** Format milliseconds as a broadcast timecode. Negative values clamp to zero. */
export function formatTimecode(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}
